/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { getLinkForExclusiveOption, getLinkForMultipleChoiceOption, getNextPageLinkForPage } from './question_utils';
import { pageTypeAllowsContent, pageTypeAllowsTooltip } from './runAlgorithm';
import {
  PageCalculatorType,
  PageMultipleQuestionsType,
  PageTypeEnum,
  PageSwimLaneType,
  PageType,
  QuestionType,
  LinkSlugType,
} from './types';

type walkPageLinkInPageCbType = (link: LinkSlugType, link_description: string, rawObject: any) => void;
type WalkPageContentInPageCbType = (content: string, content_description: string) => void;

const NEXT_LINK_DESCRIPTION = 'next link';
/**
 * Call the call for all links and html content on a page, but NOT looking inside the content html for links.
 * This is used to find all the links that a page can lead to, for highlighting.
 *
 * If you want to look inside the content html for links, you can set cbContent to a function that does that
 * see @see processContent() in brokenHearts_utils.tsx for an example that does exactly that.
 * Or just use @see getAllLinksInPageForBrokenHearts() directly.
 */
export function walkPage(page: PageType, cbLink?: walkPageLinkInPageCbType, cbContent?: WalkPageContentInPageCbType) {
  if (pageTypeAllowsContent(page.pageType) && page.content) {
    cbContent?.(page.content, 'content');
  }
  switch (page.pageType) {
    case PageTypeEnum.GUIDE:
    case PageTypeEnum.TABS:
      break;
    case PageTypeEnum.MULTIPLE_QUESTIONS:
    case PageTypeEnum.CALCULATOR:
      walkPageLinksInMultipleQuestions(page, cbLink, cbContent);
      break;
    case PageTypeEnum.SWIMLANE:
      walkPageLinksInForSwimlane(page, cbLink, cbContent);
      break;
    case PageTypeEnum.IF:
      if (page.true_link) {
        cbLink?.({ link: page.true_link.trim(), link_slug: page.true_link_slug }, 'True Link', page);
      }
      if (page.false_link) {
        cbLink?.({ link: page.false_link.trim(), link_slug: page.false_link_slug }, 'False Link', page);
      }
      break;
    // Don't add a default, so that we get an error if we miss any cases
  }
  const next_link = getNextPageLinkForPage(page);
  if (next_link) {
    cbLink?.(next_link, NEXT_LINK_DESCRIPTION, page);
  }
  if (pageTypeAllowsTooltip(page.pageType) && page.tooltip) {
    cbContent?.(page.tooltip, 'page tooltip');
  }
}

/** Not currently used, but left in since it could be useful */
export function getAllLinksInPage(page: PageType, excludeNextLink = false) {
  const linksFound = new Set<string>();
  walkPage(
    page,
    (link, description) => (!excludeNextLink || description === NEXT_LINK_DESCRIPTION) && linksFound.add(link.link)
  );
  return linksFound;
}

function walkPageLinksInForSwimlane(
  page: PageSwimLaneType,
  cbLink: walkPageLinkInPageCbType | undefined,
  cbContent: WalkPageContentInPageCbType | undefined
) {
  page.lanes?.forEach((lane, laneIndex) => {
    if (!lane.mergeLeft) {
      if (lane.link)
        cbLink?.({ link: lane.link.trim(), link_slug: lane.link_slug }, `link in lane ${laneIndex + 1}`, lane);
      if (lane.content) {
        cbContent?.(lane.content, `content in lane ${laneIndex + 1}`);
      }
    }
  });
  if (page.fallback_link)
    cbLink?.({ link: page.fallback_link.trim(), link_slug: page.fallback_link_slug }, `fallback link from swimlane`, {
      pageType: page.pageType,
      fallback_link: page.fallback_link,
      fallback_link_slug: page.fallback_link_slug,
    });
}

function walkPageLinksInQuestion(
  question: QuestionType,
  page: PageMultipleQuestionsType | PageCalculatorType,
  questionIndex: number,
  cbLink: walkPageLinkInPageCbType | undefined,
  cbContent: WalkPageContentInPageCbType | undefined
) {
  if (question.tooltip) cbContent?.(question.tooltip, `tooltip text in question ${questionIndex + 1}`);
  if (question.type === 'exclusive') {
    question.options.forEach((option, optionIndex) => {
      const link = getLinkForExclusiveOption(option, page);
      const description = `question ${questionIndex + 1}, option ${optionIndex + 1}`;
      if (link) cbLink?.(link, description, option);
      if (option.summarytext) cbContent?.(option.summarytext, `summary text in ${description}`);
      if (option.tooltip) cbContent?.(option.tooltip, `tooltip text in ${description}`);
    });
  } else if (question.type === 'multiple_choice') {
    question.options.forEach((option, optionIndex) => {
      const link = getLinkForMultipleChoiceOption(option);
      const description = `question ${questionIndex + 1}, option ${optionIndex + 1}`;
      if (link) cbLink?.(link, description, option);
      if (option.summarytext) cbContent?.(option.summarytext, `summary text in ${description}`);
      if (option.tooltip) cbContent?.(option.tooltip, `tooltip text in ${description}`);
    });
  }
}

function walkPageLinksInMultipleQuestions(
  page: PageMultipleQuestionsType | PageCalculatorType,
  cbLink: walkPageLinkInPageCbType | undefined,
  cbContent: WalkPageContentInPageCbType | undefined
) {
  page.questions?.forEach((question, index) => walkPageLinksInQuestion(question, page, index, cbLink, cbContent));
  if (page.pageType === PageTypeEnum.CALCULATOR) {
    page.formulaBins?.forEach((bin, index) => {
      if (bin.link) cbLink?.({ link: bin.link, link_slug: bin.link_slug }, `bin ${index + 1}`, page.formulaBins);
    });
  }
}
